import { makeAutoObservable } from 'mobx'

export default class ObservablePromise<T> {
   options
   promise?: Promise<T | undefined> | T | undefined
   state: TPromiseState = 'initial'
   data?: T
   error?: any

   failCallback?: (error: any) => any

   constructor(
      initialValueOrPromise?: Promise<T> | T,
      options?: { setSucceeded: (obsPromise: ObservablePromise<T>) => any }
   ) {
      makeAutoObservable(this)
      this.options = options
      if (initialValueOrPromise instanceof Promise) {
         this.setPromise(initialValueOrPromise)
      } else if (initialValueOrPromise) {
         this.setSucceeded(initialValueOrPromise)
      }
   }

   get initial() {
      return this.state === 'initial'
   }
   /** promise still being executed */
   get pending() {
      return this.state === 'pending'
   }
   /** promise was fulfilled, whether with success or error */
   get fulfilled() {
      return this.state !== 'pending'
   }
   /** promise was fulfilled with error */
   get failed() {
      return this.state === 'error'
   }
   /** promise was fulfilled with success */
   get successful() {
      return this.state === 'success'
   }

   setSucceeded = (result: T | undefined) => {
      this.state = 'success'
      this.data = result
      this.error = undefined
      if (this.options?.setSucceeded) {
         this.options.setSucceeded(this)
      }

      return this
   }
   setFailed = (error: any) => {
      this.state = 'error'
      this.error = error
      this.failCallback?.(error)
      return this
   }
   setPending = () => {
      this.state = 'pending'
      return this
   }
   unset = () => {
      this.setPending()
      return this
   }
   setPromise = (newPromise: Promise<T | undefined> | T | undefined) => {
      this.setPending()

      this.promise = new Promise((resolve, reject) => {
         if (!(newPromise instanceof Promise)) {
            this.setSucceeded(newPromise)
            resolve(this.data)
         } else {
            newPromise.then(r => {
               this.setSucceeded(r)
               resolve(this.data)
            }).catch(error => {
               this.setFailed(error)
               reject(error)
            })
         }
      })

      return this.promise
   }

   onFail = (failCallback: (error: any) => any) => {
      this.failCallback = failCallback
      return this
   }


   isSuccessful(): this is Omit<MakeMandatory<ObservablePromise<T>, 'data'>, 'error'> {
      if (this.fulfilled) {
         return true
      }
      return false
   }

   isFailed(): this is Omit<MakeMandatory<ObservablePromise<T>, 'error'>, 'data'> {
      if (this.failed) {
         return true
      }
      return false
   }
}

type TPromiseState = 'initial' | 'pending' | 'error' | 'success'
